import { useState } from 'react';
import { Bell, BellRing, Check, Trash2, ExternalLink } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useStaffAlerts } from '../contexts/StaffAlertsContext';
import { usePlayerDrilldown } from './PlayerDrilldownContext';

const fmtAgo = (ts) => {
    const s = Math.max(1, Math.round((Date.now() - ts) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
};

const TONE = {
    error:   'bg-rose-500/15 border-rose-500/40 text-rose-200',
    warning: 'bg-amber-500/15 border-amber-500/40 text-amber-200',
    success: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200',
    info:    'bg-zinc-700/30 border-white/10 text-zinc-200',
};

/**
 * Bell tray that surfaces the last 10 staff alerts (auto-credits, cashout
 * escalations, new receipts). Each entry exposes the same `View` button as
 * the source toast, opening the player's drilldown in 1 click.
 *
 * Lives in the cashier + admin dashboard headers. Hidden inside a popover
 * so it never competes with the page chrome.
 */
const StaffAlertsBell = () => {
    const { alerts, unreadCount, markAllRead, clear } = useStaffAlerts();
    const { openPlayer } = usePlayerDrilldown();
    const [open, setOpen] = useState(false);
    const Icon = unreadCount > 0 ? BellRing : Bell;

    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && unreadCount > 0) setTimeout(markAllRead, 600); }}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${
                        unreadCount > 0
                            ? 'bg-[#FFD700]/15 border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/25'
                            : 'bg-zinc-800/60 border-white/10 text-zinc-400 hover:bg-zinc-700'
                    }`}
                    aria-label={`Staff alerts (${unreadCount} unread)`}
                    data-testid="staff-alerts-bell"
                >
                    <Icon className={`w-4 h-4 ${unreadCount > 0 ? 'animate-pulse' : ''}`} />
                    {unreadCount > 0 && (
                        <span
                            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center border-2 border-[#0a0a0a]"
                            data-testid="staff-alerts-bell-badge"
                        >
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-[360px] p-0 bg-[#0a0a0a] border border-white/10 text-zinc-100"
                data-testid="staff-alerts-panel"
            >
                <div className="flex items-center justify-between p-3 border-b border-white/10">
                    <div>
                        <p className="text-xs font-black uppercase tracking-wider text-[#FFD700]">Recent staff alerts</p>
                        <p className="text-[10px] text-[#A0AAB2]">Last {alerts.length}/10 · auto-clears after 10 new</p>
                    </div>
                    <div className="flex items-center gap-1">
                        {alerts.some((a) => !a.read) && (
                            <button
                                type="button"
                                onClick={markAllRead}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
                                title="Mark all read"
                            >
                                <Check className="w-3 h-3" /> Read
                            </button>
                        )}
                        {alerts.length > 0 && (
                            <button
                                type="button"
                                onClick={clear}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-rose-500/15 border border-rose-500/40 text-rose-300 hover:bg-rose-500/25"
                                title="Clear all"
                                data-testid="staff-alerts-clear"
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="max-h-[420px] overflow-y-auto divide-y divide-white/5">
                    {alerts.length === 0 ? (
                        <div className="text-center py-8 px-3">
                            <Bell className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
                            <p className="text-xs text-zinc-500">No alerts yet — you&apos;re all caught up</p>
                        </div>
                    ) : alerts.map((a) => (
                        <div
                            key={a.id}
                            className={`p-3 flex items-start gap-2 ${a.read ? 'opacity-70' : 'bg-[#FFD700]/5'}`}
                            data-testid={`staff-alert-${a.id}`}
                        >
                            <div className={`shrink-0 w-7 h-7 rounded-md border flex items-center justify-center text-sm ${TONE[a.level] || TONE.info}`}>
                                <span>{a.icon}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[12px] font-bold text-white leading-tight truncate">{a.title}</p>
                                {a.description && (
                                    <p className="text-[10px] text-zinc-400 mt-0.5 leading-snug">{a.description}</p>
                                )}
                                <div className="flex items-center justify-between gap-2 mt-1">
                                    <span className="text-[9px] text-zinc-600 uppercase tracking-wider">{fmtAgo(a.ts)}</span>
                                    {a.userId && (
                                        <button
                                            type="button"
                                            onClick={() => { openPlayer(a.userId); setOpen(false); }}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-[#FFD700]/15 border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/25"
                                            data-testid={`staff-alert-view-${a.id}`}
                                        >
                                            <ExternalLink className="w-3 h-3" /> View
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default StaffAlertsBell;
